import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ScreenSizeService } from './screen-size.service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { shuffle } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class BannerImageService {
    isMobile$ = this.screenSizeService.isMobileWidth;
    private s3bannerUrl =
        'https://marideal-partners-prod.s3.ap-southeast-1.amazonaws.com/banner-images-frontend/banners.json';

    private bannersSubject = new BehaviorSubject<Record<string, any> | null>(
        null
    );
    banners$ = this.bannersSubject.asObservable(); // Observable to listen for changes

    constructor(
        private screenSizeService: ScreenSizeService,
        private http: HttpClient
    ) {
        this.loadBannersFromS3();
    }

    // lodash shuffle algorithm to randomize an array

    private shuffleArray<T>(array: T[]): T[] {
        return shuffle(array);
    }

    loadBannersFromS3(): void {
        interface BannerImage {
            desktop: string;
            mobile: string;
            link?: string;
        }

        interface Banner {
            images: BannerImage[];
        }

        type BannerSection = Record<string, Banner>;

        type BannersData = Record<string, BannerSection>;

        this.http.get<BannersData>(this.s3bannerUrl).subscribe(
            (data) => {
                // Randomize the order of images for each banner
                Object.keys(data).forEach((sectionKey) => {
                    Object.keys(data[sectionKey]).forEach((bannerKey) => {
                        const banner = data[sectionKey][bannerKey];
                        if (banner && banner.images && banner.images.length) {
                            banner.images = this.shuffleArray(banner.images);
                        }
                    });
                });
                this.bannersSubject.next(data); // Notify all pages when banners load
            },
            (error) => {
                this.bannersSubject.next(null);
            }
        );
    }

    /** Get banners reactively (returns `Observable` to update live) */
    getBanners(
        page: string
    ): Observable<
        Record<
            string,
            { images: { desktop: string; mobile: string; link?: string }[] }
        >
    > {
        return this.banners$.pipe(
            map((banners) => banners?.[page] || {}) // Ensure no null errors
        );
    }

    /** Get a specific banner dynamically.
     * Returns the image URL of the first image in the images array.
     */
    getBanner(
        page: string,
        bannerKey: string,
        isMobile: boolean
    ): Observable<string> {
        return this.banners$.pipe(
            map(
                (banners) =>
                    banners?.[page]?.[bannerKey]?.images?.[0]?.[
                        isMobile ? 'mobile' : 'desktop'
                    ] || ''
            )
        );
    }

    /** Redirect to a banner link.
     * Uses the link of the first image in the images array.
     */
    redirectToLink(page: string, bannerKey: string): void {
        const banners = this.bannersSubject.getValue();
        const link = banners?.[page]?.[bannerKey]?.images?.[0]?.link;
        if (link) {
            window.open(link, '_blank');
        }
    }
}
